/**
 * Session keepalive.
 *
 * The server slides the session on every authenticated request, so a user
 * clicking around stays signed in. But plenty of real clinical work generates
 * no API traffic at all — reading the summary PDF, reviewing a chart, writing
 * a note in a form that doesn't autosave. Those users hit the idle timeout
 * mid-task and saw "your session expired" while actively working.
 *
 * This pings a no-op authenticated endpoint (GET /auth/session) so the server
 * reissues the cookie and the session slides.
 *
 * The security control is preserved: we ping ONLY when the user has actually
 * done something since the last ping, using the same activity timestamp
 * useIdleTimeout maintains. An abandoned tab stops pinging immediately and
 * times out exactly as before — this extends sessions for people who are
 * present, it does not keep dead sessions alive.
 *
 * Multi-tab safe: the last-ping time is shared through localStorage, so five
 * open tabs still produce one ping per interval, not five.
 */

import { useEffect } from 'react';
import { apiGet } from '../api/client';
import { ACTIVITY_STORAGE_KEY } from './useIdleTimeout';

const PING_STORAGE_KEY = 'dwms_last_ping';

// Must match the client idle timeout, which in turn should match the server's
// IDLE_TIMEOUT_MS — the cookie's own maxAge is the server value, so if the two
// drift apart with the server lower, pings would arrive too late to help.
const DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_IDLE_TIMEOUT_MS) || 15 * 60 * 1000;

// Ping at a third of the idle window, so two consecutive pings can fail
// (offline, blip) before the session is actually at risk. Floored at 60s so a
// very short configured timeout can't turn this into a request storm.
function pingIntervalFor(timeoutMs) {
    return Math.max(60 * 1000, Math.floor(timeoutMs / 3));
}

const CHECK_INTERVAL_MS = 30 * 1000;

function readTs(key) {
    try {
        const raw = localStorage.getItem(key);
        const n = raw ? Number(raw) : 0;
        return Number.isFinite(n) && n > 0 ? n : 0;
    } catch {
        return 0;
    }
}

function writeTs(key, ts) {
    try { localStorage.setItem(key, String(ts)); } catch { /* private mode */ }
}

export default function useSessionKeepalive({
    enabled = true,
    timeoutMs = DEFAULT_TIMEOUT_MS,
} = {}) {
    useEffect(() => {
        if (!enabled) return undefined;

        const interval = pingIntervalFor(timeoutMs);
        // Treat mount as a ping: the page load that got us here was itself a
        // request, so the session is already fresh.
        writeTs(PING_STORAGE_KEY, Date.now());

        let inFlight = false;

        const tick = async () => {
            if (inFlight) return;
            const now = Date.now();
            const lastPing = readTs(PING_STORAGE_KEY);
            const lastActivity = readTs(ACTIVITY_STORAGE_KEY);

            // Not yet due.
            if (now - lastPing < interval) return;
            // Nothing has happened since the last ping — let the session
            // expire on schedule. This is what keeps the idle timeout honest.
            if (lastActivity <= lastPing) return;
            // Already idle past the point of no return; the idle timer owns
            // this case and is about to sign the user out.
            if (now - lastActivity >= timeoutMs) return;

            // Claim the slot before awaiting so other tabs don't also fire.
            writeTs(PING_STORAGE_KEY, now);
            inFlight = true;
            try {
                await apiGet('auth/session');
            } catch {
                // A 401 already routed us to login via the client's
                // auth-expired event; anything else is a transient blip and
                // the next tick retries.
            } finally {
                inFlight = false;
            }
        };

        const timer = setInterval(tick, CHECK_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [enabled, timeoutMs]);
}
